/**
 * Build an `/api`-prefixed URL safely.
 *
 * The codebase has ~25 `` `/api${something}` `` template strings
 * scattered across feature components and converters. When
 * `something` is null/undefined (avatar before redux hydration,
 * attachment without a URL, fleet_manager record with no avatar),
 * the resulting URL is `/apinull` or `/apiundefined` — the browser
 * fires it as an asset GET, the dev console fills with 404 noise,
 * and the network panel becomes unreadable.
 *
 * Use this helper to centralise the guard:
 *   - `apiUrl(null)`           → undefined  (don't render src=)
 *   - `apiUrl('')`             → undefined
 *   - `apiUrl('/foo/bar')`     → '/api/foo/bar'
 *   - `apiUrl('http://x/y')`   → 'http://x/y'   (absolute kept as-is)
 *   - `apiUrl('/api/foo')`     → '/api/foo'     (already prefixed, left alone)
 *   - `apiUrl(value, fallback)` → fallback when value is empty
 *
 * Pass the second arg only when a fallback URL is wanted (e.g.
 * a default avatar PNG); otherwise the caller should treat
 * `undefined` as "don't render this src/href".
 */
export const apiUrl = (value, fallback) => {
  if (!value) return fallback;
  if (typeof value !== 'string') return `/api${value}`;
  if (value.startsWith('http')) return value;
  // Already prefixed. The avatar convertors (Api/convertors/users.js,
  // Api/convertors/timeplans.js) hand back `/api/rails/active_storage/…`
  // and several call sites add their own prefix on top, which produced
  // `/api/api/rails/…` and a 404 on every avatar it touched. The backend
  // has no `/api` route prefix — nginx strips it before Rails sees the
  // path — so a doubled prefix is never the right answer.
  if (value === '/api' || value.startsWith('/api/')) return value;
  return `/api${value}`;
};

export default apiUrl;
